<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Contracts" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            v-if="!isReadOnlyAccess"
            icon="add"
            color="primary"
            text="Add"
            :routeName="assignmentCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <AssignmentFilters
        class="px-5 mb-sm-5"
        :jobTitles="jobTitles"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></AssignmentFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="assignmentList"
          class="elevation-2"
        >
          <template v-slot:item.startAt="{ item }">
            {{ item.startAt | formatDate }}
          </template>
          <template v-slot:item.endAt="{ item }">
            {{ item.endAt | formatDate }}
          </template>
          <template v-slot:item.active="{ item }">
            <v-checkbox
              v-model="item.active"
              class="py-0"
              :disabled="isReadOnlyAccess || !item.active"
              @click="showCloseAssignmentModal(item)"
            ></v-checkbox>
          </template>
          <template v-slot:item.overtimeEligible="{ item }">
            <v-simple-checkbox
              v-model="item.overtimeEligible"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              style="margin: 1px 1.5px"
              color="primary"
              outlined
              @click.prevent="editAssignment($event, item)"
            >
              <v-icon color="primary">{{ actionIcon }}</v-icon>
            </v-btn>
            <v-btn
              v-if="isAdmin"
              class="mx-1 my-1"
              color="red"
              outlined
              @click="deleteAssignmentHandler(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  style="margin: 1px 1.5px"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!item.overtimeEligible"
                  @click="showOvertimeEntriesListModal(item)"
                >
                  <v-icon color="primary">work_history</v-icon>
                </v-btn>
              </template>
              <span>Show Overtime Entries</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  style="margin: 1px 1.5px"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="showOneOffFeesRecordsListModal(item)"
                >
                  <v-icon color="primary">receipt_long</v-icon>
                </v-btn>
              </template>
              <span>Show One Off Fees Records</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <OvertimeEntriesListModal
      :assignmentId="assignmentId"
      :consultantName="consultantName"
      :clientName="clientName"
      :hourlyPayRate="hourlyPayRate"
      :hourlyBillRate="hourlyBillRate"
      :showDateRangePicker="true"
      :readonly="isReadOnlyAccess"
      ref="OvertimeEntriesListModal"
    ></OvertimeEntriesListModal>
    <OneOffFeesRecordListModal
      :assignmentId="assignmentId"
      :userName="consultantName"
      :clientName="clientName"
      :showDateRangePicker="true"
      :readonly="isReadOnlyAccess"
      ref="OneOffFeesRecordListModal"
    >
    </OneOffFeesRecordListModal>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected contract?'"
      :toolbarColor="'primary'"
      @cancel-click="() => {
        this.showDeleteConfirmationDialog = false
        this.contractIdToDelete = null
      }"
      @ok-click="confirmDelete"
      :showModal="showDeleteConfirmationDialog"
    ></ConfirmationModal>
    <CloseAssignmentModal
      ref="closeAssignmentModal"
      @cancel="cancelHandler"
      @closedOut="closedOutHandler"
    ></CloseAssignmentModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { assignmentCreateRoute, assignmentEditRoute } from '@/lib/constants/routes'
import AssignmentFilters from './AssignmentFilters'
import { ButtonAdd, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { pageableAndSortable } from '@/lib/pagingAndSorting'
import { ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'
import OvertimeEntriesListModal from '@/components/overtime/OvertimeEntriesListModal'
import OneOffFeesRecordListModal from '@/components/oneOffFeesRecords/OneOffFeesRecordListModal'
import { ConfirmationModal } from '@/components/dialogs'
import CloseAssignmentModal from './CloseAssignmentModal'

export default {
  mixins: [
    pageableAndSortable
  ],
  components: {
    BottomSpacer,
    AssignmentFilters,
    ButtonAdd,
    ButtonCustomIcon,
    SimplePageHeader,
    OvertimeEntriesListModal,
    OneOffFeesRecordListModal,
    ConfirmationModal,
    CloseAssignmentModal
  },
  data () {
    return {
      assignmentCreateRoute,
      assignmentInfo: {},
      assignmentId: null,
      consultantName: null,
      clientName: null,
      hourlyPayRate: null,
      hourlyBillRate: null,
      activeAssignment: null,
      jobTitles: [],
      showDeleteConfirmationDialog: false,
      contractIdToDelete: null
    }
  },
  computed: {
    ...mapFields('assignments', [
      'assignmentList'
    ]),
    ...mapFields('assignmentFilters', [
      'includeInactive'
    ]),
    isReadOnlyAccess: {
      get () {
        return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
      }
    },
    actionIcon () {
      return this.isReadOnlyAccess ? 'visibility' : 'edit'
    },
    headers: {
      get () {
        return [
          { text: 'Client', value: 'clientName', width: '14%', cellClass: 'px-0 pl-3' },
          { text: 'Consultant', value: 'userFullName', width: '14%', cellClass: 'px-0 pl-2' },
          { text: 'Job Title', value: 'jobTitle', width: '14%', cellClass: 'px-0 pl-2' },
          { text: 'Start', value: 'startAt', align: 'center', width: '8%', class: 'hide-on-sm px-0', cellClass: 'hide-on-sm px-1' },
          { text: 'End', value: 'endAt', align: 'center', width: '8%', class: 'hide-on-sm px-0', cellClass: 'hide-on-sm px-1' },
          { text: 'Active', value: 'active', align: 'center', width: '5%', class: 'px-1' },
          {
            text: 'Overtime Eligible',
            value: 'overtimeEligible',
            align: 'center',
            class: 'hide-on-sm px-0',
            cellClass: 'hide-on-sm',
            width: '10%'
          },
          {
            text: 'Actions',
            value: 'action',
            align: 'center',
            sortable: false,
            width: '25%',
            class: 'px-0',
            cellClass: 'px-2'
          }
        ]
      }
    },
    isAdmin () {
      return userIsAdmin()
    }
  },
  methods: {
    userHasRoles,
    getCurrentUser,
    ...mapActions('assignments', [
      'getFieldsWithFilters'
    ]),
    ...mapActions('assignment', [
      'deleteAssignment'
    ]),
    ...mapActions('assignmentFilters', [
      'clearFilters'
    ]),
    editAssignment (event, assignment) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: assignmentEditRoute.name, params: { id: assignment.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: assignmentEditRoute.name, params: { id: assignment.id } })
      }
    },
    handleSearchClick () {
      this.getFieldsWithFilters().then(() => {
        this.assignmentList = this.assignmentList.filter(
          (assignment) => (assignment.active || assignment.active === !this.includeInactive))
      })
    },
    showOvertimeEntriesListModal (assignment) {
      this.assignmentId = assignment.id
      this.consultantName = assignment.userFullName
      this.clientName = assignment.clientName
      this.hourlyPayRate = assignment.hourlyPayRate
      this.hourlyBillRate = assignment.hourlyBillRate
      this.activeAssignment = assignment.active
      this.$refs.OvertimeEntriesListModal.showModal()
    },
    showOneOffFeesRecordsListModal (item) {
      this.assignmentId = item.id
      this.consultantName = item.userFullName
      this.clientName = item.clientName
      this.activeAssignment = item.active
      this.$refs.OneOffFeesRecordListModal.showModal()
    },
    deleteAssignmentHandler (item) {
      this.contractIdToDelete = item.id
      this.showDeleteConfirmationDialog = true
    },
    confirmDelete () {
      this.deleteAssignment(this.contractIdToDelete).then(() => {
        this.showDeleteConfirmationDialog = false
        this.getFieldsWithFilters().then(() => {
          this.assignmentList = this.assignmentList.filter(
            (assignment) => (assignment.active || assignment.active === !this.includeInactive)
          )
        })
      })
    },
    showCloseAssignmentModal (item) {
      if (!item.active) {
        this.$refs.closeAssignmentModal.showModal(item)
      }
    },
    cancelHandler (id) {
      let assignment = this.assignmentList.find(assignment => assignment.id === id)
      assignment.active = true
    },
    closedOutHandler () {
      this.getFieldsWithFilters().then(() => {
        this.assignmentList = this.assignmentList.filter(
          (assignment) => (assignment.active || assignment.active === !this.includeInactive)
        )
      })
    }
  },
  created () {
    this.getFieldsWithFilters().then(() => {
      this.assignmentList = this.assignmentList.filter(
        (assignment) => (assignment.active || assignment.active === !this.includeInactive)
      )
      this.jobTitles = this.assignmentList.map((assignment) => { return { jobTitle: assignment.jobTitle ? assignment.jobTitle.trim() : null } })
      this.jobTitles = this.jobTitles.filter(item => {
        if (item.jobTitle !== null && item.jobTitle !== '') {
          return true
        }
        return false
      })
      this.jobTitles.sort((a, b) => (a.jobTitle > b.jobTitle) ? 1 : -1)
    })
  }
}
</script>
